import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';

interface Campaign {
  id: string;
  nome: string;
}

interface DuplicateCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  campaign: Campaign | null;
}

export function DuplicateCampaignModal({
  isOpen,
  onClose,
  onSuccess,
  campaign,
}: DuplicateCampaignModalProps) {
  const [loading, setLoading] = useState(false);
  const [nome, setNome] = useState('');

  useEffect(() => {
    if (campaign && isOpen) {
      setNome(`${campaign.nome} - Cópia`);
    }
  }, [campaign, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!campaign) return;

    if (!nome.trim()) {
      toast.error('Nome da campanha é obrigatório');
      return;
    }

    try {
      setLoading(true);
      await apiService.duplicateCampaign(campaign.id, nome.trim());
      toast.success('Campanha duplicada com sucesso');
      onSuccess();
      onClose();
      setNome('');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro ao duplicar campanha';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setNome('');
    onClose();
  };

  if (!isOpen || !campaign) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Duplicar Campanha</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-600 mb-2">
            Duplicar campanha: <strong>{campaign.nome}</strong>
          </p>
          <p className="text-xs text-gray-500">
            A nova campanha será criada com as mesmas configurações de mensagem, mas com status &quot;Pendente&quot; e sem categorias, conexões WhatsApp ou agendamento.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nome da Nova Campanha
            </label>
            <input
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Digite o nome da nova campanha"
              required
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Duplicando...' : 'Duplicar Campanha'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
